// Custom dashboards on a real grid (react-grid-layout): 12 columns, rows ROW px tall. Each widget
// has a position (x, y) and a size (w columns, h rows). Heights fit the widget's content unless the
// trader stretched it (hLocked). Older layouts (just an order and S/M/L sizes, with empty "gap"
// slots) are turned into positions here, and the empty spaces left on the grid become "+ Add
// widget" slots.
export const COLS = 12;
export const ROW = 10;
export const MARGIN = 14;
export const SLOT_W = 3;
export const SLOT_H = 8;
export const WIDTH = { small: 3, medium: 6, large: 12 };

// A first guess at each widget's height in rows, before its content is measured.
const GUESS = { calendar: 32, glance: 22, score: 40, summary: 40, tradeLists: 20, insightRow: 9, account: 12, scoreBar: 7, kpi: 12 };
export const guessH = (id, w) => GUESS[id] || (w >= 6 ? 14 : 9);

export const sizeOfW = (w) => (w <= 3 ? 'small' : w <= 6 ? 'medium' : 'large');
const isGap = (w) => typeof w.id === 'string' && /^gap/.test(w.id);
export const placed = (w) => Number.isInteger(w.x) && Number.isInteger(w.y) && Number.isInteger(w.w) && Number.isInteger(w.h);

// Older layouts: widgets in reading order, rows as tall as their tallest widget, gaps kept empty.
export function toGrid(list) {
  if (list.every((w) => isGap(w) || placed(w))) return list.filter((w) => !isGap(w));
  const out = [];
  let x = 0;
  let y = 0;
  let rowH = 0;
  for (const item of list) {
    const w = placed(item) ? item.w : WIDTH[item.size] || 12;
    const h = placed(item) ? item.h : isGap(item) ? SLOT_H : guessH(item.id, w);
    if (x + w > COLS) { y += rowH; x = 0; rowH = 0; }
    if (!isGap(item)) out.push({ ...item, x, y, w, h, size: sizeOfW(w) });
    x += w;
    rowH = Math.max(rowH, h);
    if (x >= COLS) { y += rowH; x = 0; rowH = 0; }
  }
  return out;
}

// Push widgets up so nothing floats above an empty space (the same "compact upward" the grid does).
export function compact(list) {
  const sorted = [...list].sort((a, b) => a.y - b.y || a.x - b.x);
  const done = [];
  for (const item of sorted) {
    let y = 0;
    for (const o of done) {
      if (o.x < item.x + item.w && item.x < o.x + o.w) y = Math.max(y, o.y + o.h);
    }
    done.push({ ...item, y });
  }
  return done;
}

// Empty spaces, as SLOT_W x SLOT_H "+ Add widget" slots: every free block beside or between
// widgets, then rows below until there are at least `min` slots and one fully empty row.
export function freeSlots(list, min = 16) {
  const bottom = list.reduce((m, w) => Math.max(m, w.y + w.h), 0);
  const taken = (x, y) => list.some((w) => w.x < x + SLOT_W && x < w.x + w.w && w.y < y + SLOT_H && y < w.y + w.h);
  const slots = [];
  let y = 0;
  let emptyRowsBelow = 0;
  while (y < bottom || slots.length < min || emptyRowsBelow < 1) {
    let free = 0;
    for (let x = 0; x < COLS; x += SLOT_W) {
      if (!taken(x, y)) { slots.push({ x, y }); free += 1; }
    }
    if (y >= bottom && free === COLS / SLOT_W) emptyRowsBelow += 1;
    y += SLOT_H;
    if (y > bottom + SLOT_H * 8) break;
  }
  return slots;
}

// Rows needed to show `px` of content.
export const rowsFor = (px) => Math.max(3, Math.ceil((px + MARGIN) / (ROW + MARGIN)));

// Phones and lists: widgets in reading order.
export const readingOrder = (list) => [...list].sort((a, b) => a.y - b.y || a.x - b.x);
